import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import { Tag } from "@flixbus/honeycomb-react";
import {
  Panel,
  PanelHeader,
  PanelContent,
  PanelFooter,
  PanelFooterColumn,
  ButtonGroup,
  Button,
  Select,
  SelectOption,
  Infobox,
  Heading,
} from "@flixbus/honeycomb-react";
import {
  Icon,
  IconArrowLeft,
  IconInfoSolid,
  IconQuestionSolid,
} from "@flixbus/honeycomb-icons-react";
import { panelId } from "../CostPanel.constants";
import {
  FORMULA_EMPTY,
  FORMULA_GRAMMAR_INVALID,
  FORMULA_UNKNOWN_VARIABLE,
} from "../../../formula/formulaErrorCodes";
import type { FormulaValidationResult } from "../../../formula/validateFormula";
import type { FormulaTemplateOption } from "../../../hooks/useFormulaTemplates";
import CompositionArea from "./CompositionArea/CompositionArea.component";
import Palette from "./Palette/Palette.component";
import DiscardDialog from "./DiscardDialog/DiscardDialog.component";
import { formulaCollisionDetection } from "./formulaCollisionDetection";
import { resolveInsert } from "./resolveInsert";
import {
  CUSTOM_OPTION_VALUE,
  operators,
  type FormulaChipModel,
  type PaletteDragData,
  type PaletteVariable,
  type Token,
  type TokenKind,
} from "./FormulaEditor.types";
import costPanelStyles from "../CostPanel.module.scss";
import styles from "./FormulaEditor.module.scss";

export interface FormulaEditorProps {
  active: boolean;
  panelTitle: string;
  templates: FormulaTemplateOption[];
  paletteVariables: PaletteVariable[];
  opDataVariables: PaletteVariable[];
  chipNameByCode: Record<string, string>;
  isLoadingKamVariables: boolean;
  isLoadingOpData: boolean;
  allowedCodes: string[];
  opDataCodes: string[];
  tokens: Token[];
  isCustom: boolean;
  isSelectionPending: boolean;
  isDirty: boolean;
  selectedTemplateId: string | null;
  validation: FormulaValidationResult;
  isSaveDisabled: boolean;
  isSaving: boolean;
  onClose: () => void;
  onToggleFormulaMode: () => void;
  onAddToken: (kind: TokenKind, value: string) => void;
  onInsertToken: (
    kind: TokenKind,
    value: string,
    beforeUid: string | null,
  ) => void;
  onRemoveToken: (uid: string) => void;
  onReorder: (activeUid: string, beforeUid: string | null) => void;
  onClearAll: () => void;
  onSelectTemplate: (template: FormulaTemplateOption) => void;
  onSelectCustom: () => void;
  onSave: () => void;
}

const operatorLabelByValue = new Map<string, string>(
  operators.map((op) => [op.value, op.label]),
);

const validationMessage = (result: FormulaValidationResult): string | null => {
  if (result.ok) return null;
  if (result.code === FORMULA_EMPTY) {
    return "Formula must not be empty.";
  }
  if (result.code === FORMULA_GRAMMAR_INVALID) {
    return "Invalid formula: only + - * / ( ) and variable codes are allowed.";
  }
  if (result.code === FORMULA_UNKNOWN_VARIABLE) {
    const available = result.availableCodes.length
      ? ` Available: ${result.availableCodes.join(", ")}.`
      : "";
    return `Unknown variable ${result.unknownCode} — not in this cost bucket's variables.${available}`;
  }
  return null;
};

const FormulaEditor: React.FC<FormulaEditorProps> = ({
  active,
  panelTitle,
  templates,
  paletteVariables,
  opDataVariables,
  chipNameByCode,
  isLoadingKamVariables,
  isLoadingOpData,
  allowedCodes,
  opDataCodes,
  tokens,
  isCustom,
  isSelectionPending,
  isDirty,
  selectedTemplateId,
  validation,
  isSaveDisabled,
  isSaving,
  onClose,
  onToggleFormulaMode,
  onAddToken,
  onInsertToken,
  onRemoveToken,
  onReorder,
  onClearAll,
  onSelectTemplate,
  onSelectCustom,
  onSave,
}) => {
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null);
  const [activeDragLabel, setActiveDragLabel] = useState<string | null>(null);
  const [insertionBarPosition, setInsertionBarPosition] = useState<
    string | null
  >(null);
  const [isOverArea, setIsOverArea] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const knownSet = useMemo(
    () => new Set([...allowedCodes, ...opDataCodes]),
    [allowedCodes, opDataCodes],
  );
  const nameByCode = useMemo(
    () => new Map(Object.entries(chipNameByCode)),
    [chipNameByCode],
  );

  const isLoadingVariables = isLoadingKamVariables || isLoadingOpData;

  const chips = useMemo<FormulaChipModel[]>(
    () =>
      tokens.map((token) => {
        const isVar = token.kind === "var";
        return {
          token,
          label: isVar
            ? (nameByCode.get(token.value) ?? token.value)
            : (operatorLabelByValue.get(token.value) ?? token.value),
          isInvalidVariable:
            isVar && !isLoadingVariables && !knownSet.has(token.value),
        };
      }),
    [tokens, nameByCode, knownSet, isLoadingVariables],
  );

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as PaletteDragData | undefined;
    if (data?.source === "palette") {
      setActiveDragLabel(
        data.kind === "var"
          ? (nameByCode.get(data.value) ?? data.value)
          : data.value,
      );
      return;
    }
    const chip = chips.find((c) => c.token.uid === event.active.id);
    setActiveDragLabel(chip?.label ?? null);
  };

  const resolveInsertForEvent = (event: DragOverEvent | DragEndEvent) => {
    const pointerX =
      (event.activatorEvent as PointerEvent).clientX + event.delta.x;
    return resolveInsert(
      event.over,
      pointerX,
      chips.map((c) => c.token.uid),
    );
  };

  const handleDragOver = (event: DragOverEvent) => {
    setIsOverArea(event.over !== null);
    setInsertionBarPosition(resolveInsertForEvent(event).barTarget);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragLabel(null);
    setInsertionBarPosition(null);
    setIsOverArea(false);

    const { active, over } = event;
    if (!over) return;

    const { beforeUid } = resolveInsertForEvent(event);
    const data = active.data.current as PaletteDragData | undefined;
    if (data?.source === "palette") {
      onInsertToken(data.kind, data.value, beforeUid);
      return;
    }
    if (beforeUid !== active.id) {
      onReorder(String(active.id), beforeUid);
    }
  };

  const applySelection = (value: string) => {
    if (value === CUSTOM_OPTION_VALUE) {
      onSelectCustom();
      return;
    }
    const template = templates.find((t) => t.id === value);
    if (template) onSelectTemplate(template);
  };

  const handleSelectChange = (value: string) => {
    if (isDirty) {
      setPendingSwitch(value);
      return;
    }
    applySelection(value);
  };

  const confirmSwitch = () => {
    if (pendingSwitch !== null) applySelection(pendingSwitch);
    setPendingSwitch(null);
  };

  const selectValue = isSelectionPending
    ? ""
    : isCustom
      ? CUSTOM_OPTION_VALUE
      : (selectedTemplateId ?? "");
  const errorMessage = validationMessage(validation);

  return (
    <>
      <Panel
        id={panelId}
        active={active}
        extraClasses={costPanelStyles.panelWrapper}
      >
        <PanelHeader
          backButtonProps={{
            label: "Back to inputs",
            InlineIcon: IconArrowLeft,
            onClick: onToggleFormulaMode,
          }}
          closeButtonProps={{ label: "Close", onClick: onClose }}
        >
          <div className={styles.headerContent}>
            <Heading size={3} sectionHeader flushSpace>
              {panelTitle}
            </Heading>
            <div className={styles.headerIcons}>
              <Button display="square" appearance="link" aria-label="Info">
                <Icon InlineIcon={IconInfoSolid} aria-hidden="true" />
              </Button>
              <Button display="square" appearance="link" aria-label="Help">
                <Icon InlineIcon={IconQuestionSolid} aria-hidden="true" />
              </Button>
            </div>
          </div>
        </PanelHeader>
        <PanelContent>
          <div className={styles.editorBody}>
            <Select
              id="formula-template-picker"
              label="Formula template"
              value={selectValue}
              onChange={(e) => handleSelectChange(e.target.value)}
            >
              <SelectOption value="" disabled>
                {"— Select —"}
              </SelectOption>
              {templates.map((t) => (
                <SelectOption key={t.id} value={t.id}>
                  {t.name}
                </SelectOption>
              ))}
              <SelectOption value={CUSTOM_OPTION_VALUE}>
                Create a custom formula
              </SelectOption>
            </Select>

            {isSelectionPending && (
              <Infobox appearance="info" small>
                Loading formula templates...
              </Infobox>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={formulaCollisionDetection}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={() => {
                setActiveDragLabel(null);
                setInsertionBarPosition(null);
                setIsOverArea(false);
              }}
            >
              <CompositionArea
                chips={chips}
                isCustom={isCustom}
                insertionBarPosition={insertionBarPosition}
                isOver={isOverArea}
                onRemove={onRemoveToken}
                onClearAll={onClearAll}
              />

              {errorMessage && (
                <Infobox appearance="danger" small>
                  {errorMessage}
                </Infobox>
              )}

              <Palette
                kamVariables={paletteVariables}
                opDataVariables={opDataVariables}
                isLoading={isLoadingVariables}
                onAdd={onAddToken}
              />

              {createPortal(
                <DragOverlay modifiers={[snapCenterToCursor]} zIndex={1300}>
                  {activeDragLabel !== null ? (
                    <Tag display="outlined">{activeDragLabel}</Tag>
                  ) : null}
                </DragOverlay>,
                document.body,
              )}
            </DndContext>
          </div>
        </PanelContent>
        <PanelFooter extraClasses={styles.footer}>
          <PanelFooterColumn>
            <ButtonGroup>
              <Button display="block" onClick={onToggleFormulaMode}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                display="block"
                loading={isSaving}
                disabled={isSaveDisabled || isSaving}
                onClick={onSave}
              >
                Save formula
              </Button>
            </ButtonGroup>
          </PanelFooterColumn>
        </PanelFooter>
      </Panel>
      <DiscardDialog
        active={pendingSwitch !== null}
        onKeepEditing={() => setPendingSwitch(null)}
        onDiscard={confirmSwitch}
      />
    </>
  );
};

export default FormulaEditor;
